// ── ONE INVOICE, PULLED OUT OF A STATEMENT ───────────────────────────────
//
// A statement is a list of LINES. An invoice is every line raised under one
// number, which is how it was written and how the customer reads it. Julian
// wants the number and the description on the statement to open that
// document, in a tab of its own, so he and the customer can both see what
// the money is for.
//
// The grouping already existed inside ClientProfile, built from a client's
// whole history. This is the same rule against ONE project, so the statement
// link and the invoice list cannot drift into disagreeing about what
// INV-2026-1880 contains.
//
// Two things it must get right:
//
//   A discount and a tax are not work. They ride under the same number as
//   the trade they adjust, and counting them as items would make a one-trade
//   bill read as three.
//
//   The bill is dated by its EARLIEST line. They are written in one go, but
//   a line added to the invoice later must not move the day it went out.

export const isMoneyLine = (l) =>
  ['discount', 'tax'].includes(String((l && l.trade) || '').trim().toLowerCase());

// What identifies the bill a line belongs to. A line with no number is its
// own bill: it has never been grouped with anything and inventing a group
// for it would put two unrelated trades on one document.
export const invoiceKeyOf = (l) =>
  String((l && l.invoiceNumber) || '').trim() || `line:${(l && l._id) || ''}`;

/**
 * Every line on `project` that belongs to the invoice `key` names, as one
 * document.
 *
 * @param {object} project  as the server sent it, admin or client view.
 * @param {string} key      an invoice number, or `line:<id>` for an unnumbered
 *                          line, which is what invoiceKeyOf gives.
 * @returns {object|null}   null when the project has no such invoice, so the
 *   caller can say so rather than drawing an empty sheet.
 */
export function invoiceFromProject(project, key) {
  const wanted = String(key == null ? '' : key).trim();
  if (!project || !wanted) return null;

  const lines = (Array.isArray(project.lines) ? project.lines : [])
    .filter(Boolean)
    .filter((l) => invoiceKeyOf(l) === wanted);
  if (!lines.length) return null;

  let total = 0;
  let paid = 0;
  let owed = 0;
  let settled = true;
  let items = 0;
  let date = null;

  for (const l of lines) {
    if (!isMoneyLine(l)) items += 1;
    total += Number(l.amount) || 0;
    // The server decorates every line with its own paid and owed. A project
    // loaded without them leaves `settled` false, and the caller says nothing
    // rather than guessing a bill is unpaid when it is not.
    if (typeof l.paid === 'number' && typeof l.owed === 'number') {
      paid += l.paid;
      owed += l.owed;
    } else {
      settled = false;
    }
    if (l.invoiceDate && (!date || new Date(l.invoiceDate) < new Date(date))) {
      date = l.invoiceDate;
    }
  }

  return {
    key: wanted,
    // Blank for an unnumbered line: the document says "Invoice" rather than
    // printing the internal id of a row.
    number: String(lines[0].invoiceNumber || '').trim(),
    date,
    lines,
    items,
    total,
    paid,
    owed,
    settled,
    kind: 'project',
    // The sheet prints the job it belongs to and needs the pay token to put
    // a link on the page. Carried whole rather than picked apart here.
    project,
  };
}
